import { RoutePath } from 'enums/routePath';

import { EventAction } from './types';

interface EventActionToggle {
  [EventAction.CHANGE]?: boolean;
  [EventAction.CLICK]?: boolean;
  [EventAction.SUBMIT]?: boolean;
}

interface ChangeActionToggle extends EventActionToggle {
  [EventAction.CHANGE]: boolean;
}

interface ClickActionToggle extends EventActionToggle {
  [EventAction.CLICK]: boolean;
}

interface SubmitActionToggle extends EventActionToggle {
  [EventAction.SUBMIT]: boolean;
}

interface ChangeClickActionToggle extends EventActionToggle {
  [EventAction.CHANGE]: boolean;
  [EventAction.CLICK]: boolean;
}

interface ClickSubmitActionToggle extends EventActionToggle {
  [EventAction.CLICK]: boolean;
  [EventAction.SUBMIT]: boolean;
}
interface EventActionConfig {
  /**
   * Globally defined for every filter on a page;
   */
  globalFilterActions: ChangeClickActionToggle;
  createOpportunities?: ClickSubmitActionToggle;
  createSegment?: ClickSubmitActionToggle;
  dateRange?: ChangeActionToggle;
  exportCSV?: ClickActionToggle;
  exportProspectsCSV?: ClickActionToggle;
  exportHaveTargetSolutionCSV?: ClickActionToggle;
  saveSegment?: ClickActionToggle;
  showSegment?: ChangeClickActionToggle;
  userSelect?: ChangeActionToggle;
  viewSegment?: ClickActionToggle;
  summaryCardLink?: ClickActionToggle;
}

export type EventActionConfigKey = keyof EventActionConfig;

const filterAction: ChangeClickActionToggle = {
  [EventAction.CHANGE]: true,
  [EventAction.CLICK]: false,
};

const clickAction: ClickActionToggle = {
  [EventAction.CLICK]: true,
};

const changeAction: ChangeActionToggle = {
  [EventAction.CHANGE]: true,
};

const submitAction: SubmitActionToggle = {
  [EventAction.SUBMIT]: true,
};

const changeClickAction = {
  ...changeAction,
  ...clickAction,
};

const clickSubmitAction = {
  ...clickAction,
  ...submitAction,
};

const defaultGlobalFilterActions = {
  globalFilterActions: {
    ...filterAction,
  },
};

const segmentsConfig = {
  ...defaultGlobalFilterActions,
  dateRange: changeAction,
  showSegment: changeClickAction,
  userSelect: changeAction,
  viewSegment: clickAction,
};

const companiesContactsConfig = {
  ...segmentsConfig,
  createSegment: clickSubmitAction,
  exportCSV: clickAction,
  saveSegment: clickAction,
};

const salesDashboardConfig = {
  ...defaultGlobalFilterActions,
  summaryCardLink: clickAction,
};

const initEventTrackerConfig: Partial<Record<RoutePath, EventActionConfig>> = {
  [RoutePath.MEETING_PLANNER]: defaultGlobalFilterActions,
  [RoutePath.COMPANIES]: companiesContactsConfig,
  [RoutePath.COMPANY_REVENUE_HISTORY]: defaultGlobalFilterActions,
  [RoutePath.COMPANY_PROFITABILITY]: defaultGlobalFilterActions,
  [RoutePath.COMPANY_PROFITABILITY_CUMULATIVE]: defaultGlobalFilterActions,
  [RoutePath.CONTACTS]: companiesContactsConfig,
  [RoutePath.HOME]: {
    ...defaultGlobalFilterActions,
    showSegment: changeClickAction,
    viewSegment: clickAction,
  },
  [RoutePath.PROFITABILITY_ACCOUNT]: defaultGlobalFilterActions,
  [RoutePath.PROFITABILITY_CUSTOMER]: defaultGlobalFilterActions,
  [RoutePath.PROSPECTING_BUILD]: {
    ...defaultGlobalFilterActions,
    createOpportunities: clickSubmitAction,
    exportProspectsCSV: clickAction,
    exportHaveTargetSolutionCSV: clickAction,
  },
  [RoutePath.PROSPECTING_CONTRACT_TRACKER]: {
    ...defaultGlobalFilterActions,
    createOpportunities: clickSubmitAction,
  },
  [RoutePath.PROSPECTING_OPEN_OPPORTUNITIES]: {
    ...defaultGlobalFilterActions,
    createOpportunities: clickSubmitAction,
  },
  [RoutePath.PROSPECTING_SALES_DASHBOARD]: salesDashboardConfig,
  [RoutePath.SEGMENTS]: segmentsConfig,
  [RoutePath.TEAM_ACTIVITY]: defaultGlobalFilterActions,
  [RoutePath.TIERING_CUSTOMER]: defaultGlobalFilterActions,
  [RoutePath.TRACK_REVENUE]: defaultGlobalFilterActions,
  [RoutePath.TRACK_REVENUE_OVER_TIME]: defaultGlobalFilterActions,
  [RoutePath.TRACK_REVENUE_CHANGE_OVER_TIME]: defaultGlobalFilterActions,
  [RoutePath.TRACK_REVENUE_BY_PRODUCT]: defaultGlobalFilterActions,
  [RoutePath.TRACK_REVENUE_BY_CUSTOMER]: defaultGlobalFilterActions,
  [RoutePath.USER_ACTIVITY_ID]: defaultGlobalFilterActions,
  [RoutePath.WHITESPACE_ADDITIONS_OVERVIEW]: defaultGlobalFilterActions,
  [RoutePath.WHITESPACE_AGREEMENT_ADDITION_MAP]: {
    ...defaultGlobalFilterActions,
    createOpportunities: clickSubmitAction,
  },
  [RoutePath.WHITESPACE_AGREEMENT_TYPE]: {
    ...defaultGlobalFilterActions,
    createOpportunities: clickSubmitAction,
  },
  [RoutePath.WHITESPACE_PRODUCT_ADDITIONS]: {
    ...defaultGlobalFilterActions,
    createOpportunities: clickSubmitAction,
  },
};

/* eslint-disable @typescript-eslint/no-empty-function */
const initialContext = {
  eventTrackerConfig: initEventTrackerConfig,
  onClickTrackEvent: () => {},
  onChangeTrackEvent: () => {},
  onSubmitTrackEvent: () => {},
  onFilterChangeTrackEvent: () => {},
  onFilterClickTrackEvent: () => {},
};

export default initialContext;
